// Shared helpers for the Cockpit feature. Vocabulary: domain / workstream /
// block (Walker Architecture). Formatting + query helpers are ports of the
// Obsidian plugin CockpitView's ck* helpers (main.js) so the two surfaces
// render identically.

/** Mirrors the backend's DURABLE_DOMAINS in skills/_lib.py — block capture
 * into any other domain is rejected server-side by reject_legacy_domain. */
export const DURABLE_DOMAINS = ['Walker', 'Relationships', 'Enterprise', 'Capital']

/** Strip the `YYMMDD-HHMMSS ` prefix (and legacy `(NN) ` duration token). */
export function stripTimestamp(name: string): string {
  return name.replace(/^\d{6}-\d{6}\s*/, '').replace(/^\(\d+\)\s*/, '')
}

export function fmtMin(min: number): string {
  const m = Math.round(min || 0)
  if (m < 60) return `${m}m`
  const h = Math.floor(m / 60)
  const r = m % 60
  return r ? `${h}h ${r}m` : `${h}h`
}

/** "2:30p" / "9:00a" — plugin's ckFmtTime. */
export function fmtTime(iso: string | null): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  let h = d.getHours()
  const ap = h >= 12 ? 'p' : 'a'
  h = h % 12 || 12
  const mm = d.getMinutes()
  return mm ? `${h}:${String(mm).padStart(2, '0')}${ap}` : `${h}:00${ap}`
}

/** "today" / "tomorrow" / "Thu 6/18" — plugin's ckFmtDay. */
export function fmtDay(iso: string | null): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const that = new Date(d)
  that.setHours(0, 0, 0, 0)
  const diff = Math.round((that.getTime() - today.getTime()) / 86400000)
  if (diff === 0) return 'today'
  if (diff === 1) return 'tomorrow'
  return d.toLocaleDateString(undefined, { weekday: 'short', month: 'numeric', day: 'numeric' })
}

/** "just now" / "12m ago" / "3h ago" / "2d ago" — plugin's ckRelTime. */
export function relTime(iso: string | null): string {
  if (!iso) return ''
  const ms = Date.now() - new Date(iso).getTime()
  if (Number.isNaN(ms)) return ''
  const m = Math.round(ms / 60000)
  if (m < 1) return 'just now'
  if (m < 60) return `${m}m ago`
  const h = Math.round(m / 60)
  if (h < 36) return `${h}h ago`
  return `${Math.round(h / 24)}d ago`
}

/** Status → color. Plugin uses Obsidian CSS vars; these are the web
 * equivalents (Tailwind 400-series on the dark palette). */
export const STATUS_COLORS: Record<string, string> = {
  completed: '#60a5fa',
  in_progress: '#facc15',
  scheduled: '#4ade80',
  pending: '#a3a3a3',
  overdue: '#f87171',
  missing: '#fb923c',
}

/** Deterministic domain color — same hash as the plugin's ckDomainColor so
 * a domain renders the same hue in Obsidian and on the web. */
export function domainColor(name: string | null | undefined): string {
  let h = 0
  const s = String(name || '')
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0
  return `hsl(${h % 360}, 55%, 45%)`
}

/** master.md query syntax → predicate. Space = AND, OR, -term/NOT, *wild,
 * "phrase". Port of the plugin's ckCompileQuery / server's _compile_query. */
export function compileQuery(q: string): (text: string) => boolean {
  const esc = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  const toRe = (term: string, literal: boolean) => {
    if (literal || !term.includes('*')) return new RegExp(esc(term), 'i')
    return new RegExp(term.split('*').map(esc).join('.*'), 'i')
  }
  const tokens: Array<[string, boolean]> = []
  const re = /"([^"]*)"|(\S+)/g
  let m: RegExpExecArray | null
  while ((m = re.exec(q)) !== null) {
    if (m[1] !== undefined) tokens.push([m[1], true])
    else tokens.push([m[2], false])
  }
  const clauses: Array<Array<[RegExp, boolean]>> = [[]]
  let negNext = false
  for (const [tok, quoted] of tokens) {
    if (!quoted && tok.toUpperCase() === 'OR') {
      if (clauses[clauses.length - 1].length) clauses.push([])
      continue
    }
    if (!quoted && tok.toUpperCase() === 'NOT') {
      negNext = true
      continue
    }
    let negated = negNext
    negNext = false
    let body = tok
    if (!quoted && body.startsWith('-') && body.length > 1) {
      negated = true
      body = body.slice(1)
    }
    if (!body) continue
    clauses[clauses.length - 1].push([toRe(body, quoted), negated])
  }
  const live = clauses.filter((c) => c.length)
  return (text: string) => {
    if (!live.length) return true
    return live.some((clause) => clause.every(([rx, neg]) => neg !== rx.test(text)))
  }
}

/** Today's date as YYYY-MM-DD in LOCAL time (toISOString would be UTC and
 * shift the default date after 8pm ET — same class of bug as the block
 * modal's timezone gotcha). */
export function todayLocalISO(): string {
  const d = new Date()
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

/** Prep markdown for the viewer: drop frontmatter, dataviewjs fences, and
 * HTML comments (the concierge section markers `<!-- concierge:x -->` and the
 * user-content boundary render as literal text otherwise — react-markdown
 * does NOT silently drop raw HTML), and flatten wiki-links to their display
 * text (react-markdown renders [[x]] literally otherwise). */
export function prepMarkdownForView(raw: string): string {
  let s = raw
  if (s.startsWith('---\n')) {
    const end = s.indexOf('\n---', 4)
    if (end !== -1) s = s.slice(s.indexOf('\n', end + 1) + 1)
  }
  s = s.replace(/```dataviewjs[\s\S]*?```/g, '')
  s = s.replace(/<!--[\s\S]*?-->/g, '')
  s = s.replace(/\[\[([^\]|]+)\|([^\]]+)\]\]/g, '$2')
  s = s.replace(/\[\[([^\]]+)\]\]/g, '$1')
  return s.trim()
}

/** Parse an obsidian://open?path=<urlencoded> URL → absolute vault path,
 * or null when it isn't one. Calendar events and concierge:reference
 * sections carry these; on the web they open in the FileViewer instead of
 * launching Obsidian. */
export function obsidianUrlToPath(href: string): string | null {
  if (!href.startsWith('obsidian://')) return null
  const m = href.match(/[?&]path=([^&]+)/)
  if (!m) return null
  try {
    return decodeURIComponent(m[1])
  } catch {
    return null
  }
}
